/**
 * Payees API (TMMAPP-0029 / TMMAPI-0055 + 0063) — owner counterparties, aliases, merge,
 * name-on-line. Wire types from `src/api/generated/`; normalized domain out. The bank's
 * `payee` text is never rewritten (MM-D-19) — naming writes an alias / `payee_id` beside it.
 */
import { download, request, type DownloadResult } from './client';
import type {
  OwnerRegisterLine as WireOwnerRegisterLine,
  PayeeAliasApplyResponse as WireApplyResponse,
  PayeeAliasCreateRequest,
  PayeeAliasPatchRequest,
  PayeeAliasPreview as WireAliasPreview,
  PayeeAliasPreviewConflict as WirePreviewConflict,
  PayeeAliasPreviewRequest,
  PayeeAliasRemovalPreview as WireAliasRemovalPreview,
  PayeeAliasResponse as WirePayeeAlias,
  PayeeCategoryDefault as WirePayeeCategoryDefault,
  PayeeCategoryDefaultRequest,
  PayeeCreateRequest,
  PayeeDetailResponse as WirePayeeDetail,
  PayeeLine as WirePayeeLine,
  PayeeListResponse as WirePayeeList,
  PayeeMergeRequest,
  PayeeNameOnLineRequest,
  PayeeOrderAlias as WireOrderAlias,
  PayeeOrderRow as WireOrderRow,
  PayeeFromWireRequest,
  PayeeFromWireResponse as WirePayeeFromWire,
  PayeePatchRequest,
  PayeePatternReresolveResponse as WireReresolve,
  PayeePatternSuggestion as WirePatternSuggestion,
  PayeePatternSuggestRequest,
  PayeeResponse as WirePayee,
  PayeeTotals as WirePayeeTotals,
} from './generated';
import { normalizeNamingSource, type NamingSource } from './namingSource';
import {
  normalizeOwnerRegisterLine,
  normalizePayeeSource,
  type OwnerRegisterLine,
  type PayeeRef,
  type PayeeSource,
} from './transactions';

export type { PayeeRef };
export type PayeeKind = 'VENDOR' | 'PERSON' | 'OTHER';
export type PayeeStatus = 'ACTIVE' | 'ARCHIVED';
export type NameOnLineScope = 'LINE' | 'GROUP';

/** The classifier's served reason codes (TMMAPI-0157 D4), from the generated contract. */
export type CounterpartyReasonCode = NonNullable<WirePayee['reasonCode']>;
export type PaymentRail = NonNullable<WirePayee['rail']>;

/** A suggestion's reason, for catalog copy only (TMM-B3); `NONE` and unknown codes are null. */
export interface CounterpartyReason {
  code: Exclude<CounterpartyReasonCode, 'NONE'>;
  /** The rail of a `P2P_RAIL` or `CORPORATE_MARKER` reason; null when absent or unknown. */
  rail: PaymentRail | null;
}

const REASON_CODES: ReadonlySet<string> = new Set([
  'P2P_RAIL',
  'CHEQUE',
  'ACQUIRER_PREFIX',
  'CORPORATE_MARKER',
]);
const RAILS: ReadonlySet<string> = new Set([
  'ZELLE',
  'VENMO',
  'CASH_APP',
  'PAYPAL',
  'SQUARE_CASH',
  'POPMONEY',
  'INTERAC',
  'SINPE',
]);

/** Enum narrowing: `NONE` and an unknown future code read as no reason, an unknown rail as none. */
export function normalizeCounterpartyReason(
  code: string | null | undefined,
  rail: string | null | undefined,
): CounterpartyReason | null {
  if (code == null || !REASON_CODES.has(code)) return null;
  return {
    code: code as CounterpartyReason['code'],
    rail: rail != null && RAILS.has(rail) ? (rail as PaymentRail) : null,
  };
}

/**
 * How an alias matches the bank's payee text (TMMAPI-0160 U3), case-insensitively: `EXACT` one
 * normalized spelling (the default, and always first), `CONTAINS` a fragment, `REGEX` a pattern
 * (matched with `find()`).
 */
export type AliasMatchType = 'EXACT' | 'CONTAINS' | 'REGEX';
const MATCH_TYPES: ReadonlySet<string> = new Set(['EXACT', 'CONTAINS', 'REGEX']);

/** Enum narrowing: an unknown future match type reads as `EXACT`. */
export function asMatchType(value: string): AliasMatchType {
  return MATCH_TYPES.has(value) ? (value as AliasMatchType) : 'EXACT';
}

export interface PayeeAlias {
  id: number;
  matchType: AliasMatchType;
  /** An `EXACT` alias's spelling; '' for a pattern alias. */
  payeeNorm: string;
  /** A `CONTAINS` / `REGEX` alias's pattern; null for an exact one. */
  pattern: string | null;
  /** A pattern alias's place in the owner's one matching order; null for an exact one. */
  priority: number | null;
  hits: number;
  createdAt: string;
}

/** One line a pattern would name (the preview's sample: bank text and date only). */
export interface AliasPreviewSample {
  payee: string;
  postedOn: string;
}

/** Lines the pattern matches that another payee holds now. */
export interface AliasPreviewConflict {
  payeeId: number;
  payeeName: string;
  count: number;
  /**
   * The higher payee in the matching order that takes them now; null when they are never moved
   * (named by hand, by an exact alias, by AI or by a rule, or when no higher payee holds them).
   */
  byPayeeId: number | null;
}

/** `POST …/aliases/preview` (and the suggestion's own dry run). */
export interface AliasPreview {
  matchCount: number;
  sample: AliasPreviewSample[];
  conflicts: AliasPreviewConflict[];
}

/** What removing an alias does to the lines it named (TMMAPI-0181 U3). */
export type AliasRemovalLines = 'KEEP' | 'RERESOLVE';

/** `DELETE …/aliases/{aliasId}?dryRun=true`: the lines the alias named, never one named by hand. */
export interface AliasRemovalPreview {
  linesAffected: number;
  /** Of those, the lines re-matching would name for another payee. */
  wouldRename: number;
  /** Of those, the lines re-matching would leave unnamed. */
  wouldUnname: number;
  /** The payee's last pattern alias: removing it takes the payee out of the matching order (J8). */
  lastPatternAlias: boolean;
}

/** AI's proposed pattern (MM-D-45): nothing is saved until the person saves it. */
export interface PatternSuggestion extends AliasPreview {
  matchType: AliasMatchType;
  pattern: string;
  /** In the person's language. */
  explanation: string;
  confidence: number;
  source: 'AI';
}

/** One alias on a payee-order row (`GET /v1/me/payee-order`). */
export interface PayeeOrderAlias {
  aliasId: number;
  matchType: AliasMatchType;
  pattern: string;
  matchCount: number;
}

/**
 * One payee in the owner's matching order. Exact-only payees are omitted: they never compete.
 * `priority` is the 0-based place. `aliases` lists the pattern aliases that give the payee its place.
 */
export interface PayeeOrderRow {
  payeeId: number;
  payeeName: string;
  priority: number;
  matchCount: number;
  aliases: PayeeOrderAlias[];
}

export interface PayeeTotals {
  transactedMinor: number;
  transactionCount: number;
  firstOn: string | null;
  lastOn: string | null;
}

export interface Payee {
  id: number;
  name: string;
  kind: PayeeKind;
  status: PayeeStatus;
  notes: string | null;
  aliases: PayeeAlias[];
  aiExcluded: boolean;
  suggestedKind: PayeeKind | null;
  suggestedAiExcluded: boolean | null;
  /** Why the classifier suggests the privacy or kind (TMMAPI-0157 D4); null for `NONE` or unknown. */
  suggestionReason: CounterpartyReason | null;
  linkedAccountId: number | null;
  linkedAccountName: string | null;
  mergedIntoPayeeId: number | null;
  /** What everyone but the owner reads in place of this payee (MM-D-117); null when unset. */
  alternateName: string | null;
  /** Served only on a list asked `withTotals`; null otherwise. */
  totals: PayeeTotals | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayeeByEnvelope {
  envelopeId: number;
  name: string;
  amountMinor: number;
}

export interface PayeeByCategory {
  categoryId: number;
  name: string;
  amountMinor: number;
}

export interface PayeeByMonth {
  month: string;
  amountMinor: number;
}

export interface PayeeLineEnvelope {
  envelopeId: number;
  name: string;
  amountMinor: number;
}

export interface PayeeLine {
  /** The line's account and id (TMMAPI-0160 U4): moving or opening it needs both. */
  accountId: number;
  transactionId: number;
  postedOn: string;
  accountName: string;
  amountMinor: number;
  payee: string;
  envelopes: PayeeLineEnvelope[];
  /** How the line got this payee (TMM-B9: `AI` keeps the AI mark); absent when not served. */
  payeeSource?: PayeeSource;
  /**
   * How the line's payee name was made (TMMAPI-0195 item 8a; TMMAPP-0164 item 10), with
   * `OwnerRegisterLine`'s meaning: absent while unnamed and when unknown. Per line, never rolled up.
   */
  namingSource?: NamingSource;
  /** Set only when a person confirmed the line's payee name, as `OwnerRegisterLine`'s. */
  confirmedByPerson?: true;
}

export interface PayeeTransfers {
  /** Null when the payee has no linked account (the block is absent). */
  toLinkedAccountMinor: number | null;
}

/** The payee's default category in one virtual account (MM-D-126): a hint, never an override. */
export interface PayeeCategoryDefault {
  virtualAccountId: number;
  categoryId: number;
}

export interface PayeeDetail {
  payee: Payee;
  totals: PayeeTotals;
  byEnvelope: PayeeByEnvelope[];
  byCategory: PayeeByCategory[];
  byMonth: PayeeByMonth[];
  lines: PayeeLine[];
  transfers: PayeeTransfers;
  categoryDefaults: PayeeCategoryDefault[];
  nextCursor: string | null;
}

/**
 * `PATCH /v1/me/payees/{id}`: only the keys the person changed; every other key is omitted
 * (unchanged). `alternateName` is three-state (TMMAPI-0167): omitted keeps the name, `null` clears it.
 */
export type PayeePatchBody = PayeePatchRequest;

export type NameOnLineBody =
  { payeeId: number; scope?: NameOnLineScope } | { name: string; scope?: NameOnLineScope };

const KINDS: ReadonlySet<string> = new Set(['VENDOR', 'PERSON', 'OTHER']);

/** Enum narrowing: an unknown future kind reads as no suggestion. */
function asKindOrNull(value: string | undefined): PayeeKind | null {
  return value !== undefined && KINDS.has(value) ? (value as PayeeKind) : null;
}

export function normalizePayeeAlias(w: WirePayeeAlias): PayeeAlias {
  return {
    id: w.id,
    matchType: asMatchType(w.matchType),
    payeeNorm: w.payeeNorm ?? '',
    pattern: w.pattern ?? null,
    priority: w.priority ?? null,
    hits: w.hits,
    createdAt: w.createdAt,
  };
}

function normalizePreviewConflict(w: WirePreviewConflict): AliasPreviewConflict {
  return {
    payeeId: w.payeeId,
    payeeName: w.payeeName,
    count: w.count,
    byPayeeId: w.byPayeeId ?? null,
  };
}

export function normalizeAliasPreview(w: WireAliasPreview | WirePatternSuggestion): AliasPreview {
  return {
    matchCount: w.matchCount,
    sample: w.sample.map((row) => ({ payee: row.payee, postedOn: row.postedOn })),
    conflicts: w.conflicts.map(normalizePreviewConflict),
  };
}

export function normalizePatternSuggestion(w: WirePatternSuggestion): PatternSuggestion {
  return {
    ...normalizeAliasPreview(w),
    matchType: asMatchType(w.matchType),
    pattern: w.pattern,
    explanation: w.explanation,
    confidence: w.confidence,
    source: 'AI',
  };
}

function normalizeOrderAlias(w: WireOrderAlias): PayeeOrderAlias {
  return {
    aliasId: w.aliasId,
    matchType: asMatchType(w.matchType),
    pattern: w.pattern,
    matchCount: w.matchCount,
  };
}

export function normalizeOrderRow(w: WireOrderRow): PayeeOrderRow {
  return {
    payeeId: w.payeeId,
    payeeName: w.payeeName,
    priority: w.priority,
    matchCount: w.matchCount,
    aliases: w.aliases.map(normalizeOrderAlias),
  };
}

/** The order as served: by `priority`, whatever order the rows arrive in. */
export function normalizeOrderRows(wire: readonly WireOrderRow[]): PayeeOrderRow[] {
  return wire.map(normalizeOrderRow).sort((a, b) => a.priority - b.priority);
}

export function normalizePayeeTotals(w: WirePayeeTotals): PayeeTotals {
  return {
    transactedMinor: w.transactedMinor,
    transactionCount: w.transactionCount,
    firstOn: w.firstOn ?? null,
    lastOn: w.lastOn ?? null,
  };
}

export function normalizePayee(w: WirePayee): Payee {
  return {
    id: w.id,
    name: w.name,
    kind: w.kind,
    status: w.status,
    notes: w.notes ?? null,
    aliases: (w.aliases ?? []).map(normalizePayeeAlias),
    aiExcluded: w.aiExcluded,
    suggestedKind: asKindOrNull(w.suggestedKind),
    suggestedAiExcluded: w.suggestedAiExcluded ?? null,
    suggestionReason: normalizeCounterpartyReason(w.reasonCode, w.rail),
    linkedAccountId: w.linkedAccountId ?? null,
    linkedAccountName: w.linkedAccountName ?? null,
    mergedIntoPayeeId: w.mergedIntoPayeeId ?? null,
    alternateName: w.alternateName ?? null,
    totals: w.totals == null ? null : normalizePayeeTotals(w.totals),
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

/** A line's envelope parts; an Unassigned part (no `envelopeId`) is not an envelope and is left out. */
function normalizeLineEnvelopes(
  rows: readonly { envelopeId?: number; name: string; amountMinor: number }[],
): PayeeLineEnvelope[] {
  return rows.flatMap((row) =>
    row.envelopeId == null
      ? []
      : [{ envelopeId: row.envelopeId, name: row.name, amountMinor: row.amountMinor }],
  );
}

function normalizePayeeLine(w: WirePayeeLine): PayeeLine {
  const payeeSource = normalizePayeeSource(w.payeeSource);
  const namingSource = normalizeNamingSource(w.namingSource);
  return {
    accountId: w.accountId,
    transactionId: w.transactionId,
    postedOn: w.postedOn,
    accountName: w.accountName,
    amountMinor: w.amountMinor,
    payee: w.payee,
    envelopes: normalizeLineEnvelopes(w.envelopes),
    ...(payeeSource === null ? {} : { payeeSource }),
    ...(namingSource === null ? {} : { namingSource }),
    ...(w.confirmedByPerson === true ? { confirmedByPerson: true as const } : {}),
  };
}

export function normalizePayeeDetail(w: WirePayeeDetail): PayeeDetail {
  return {
    payee: normalizePayee(w.payee),
    totals: normalizePayeeTotals(w.totals),
    byEnvelope: normalizeLineEnvelopes(w.byEnvelope),
    byCategory: w.byCategory.map((row) => ({
      categoryId: row.categoryId,
      name: row.name,
      amountMinor: row.amountMinor,
    })),
    byMonth: w.byMonth.map((row) => ({ month: row.month, amountMinor: row.amountMinor })),
    lines: w.lines.map(normalizePayeeLine),
    transfers: {
      toLinkedAccountMinor: w.transfers == null ? null : w.transfers.toLinkedAccountMinor,
    },
    categoryDefaults: w.categoryDefaults.map(normalizeCategoryDefault),
    nextCursor: w.nextCursor ?? null,
  };
}

export function normalizeCategoryDefault(w: WirePayeeCategoryDefault): PayeeCategoryDefault {
  return { virtualAccountId: w.virtualAccountId, categoryId: w.categoryId };
}

/**
 * `GET /v1/me/payees` answers `PayeeListResponse` = `{ items, pendingSuggestions }`, never a bare
 * array (TMMAPP-0120 item 16: reading it as an array emptied every payee list on staging).
 */
export function normalizePayeeList(wire: WirePayeeList): Payee[] {
  return wire.items.map(normalizePayee);
}

export const payeesApi = {
  /** GET /v1/me/payees */
  async list(
    query: {
      status?: PayeeStatus;
      q?: string;
      withTotals?: boolean;
    } = {},
  ): Promise<Payee[]> {
    return normalizePayeeList(await request<WirePayeeList>('GET', '/v1/me/payees', { query }));
  },

  /** GET /v1/me/payees/{id} */
  async get(
    id: number,
    query: { from?: string; to?: string; cursor?: string } = {},
  ): Promise<PayeeDetail> {
    return normalizePayeeDetail(
      await request<WirePayeeDetail>('GET', `/v1/me/payees/${id}`, { query }),
    );
  },

  /**
   * POST /v1/me/payees/from-counterparty (TMMAPI-0170): a payee from a wire's or a Zelle line's
   * counterparty; the app sends both rails here (TMMAPP-0137; `from-wire` is its alias). `name`
   * null omits it, so the server title-cases the counterparty. 409 `NOT_A_WIRE` or
   * `OWN_ACCOUNT_WIRE`.
   */
  async fromCounterparty(
    transactionId: number,
    name: string | null,
  ): Promise<{ payeeId: number; name: string; linesResolved: number }> {
    const json: PayeeFromWireRequest = name === null ? { transactionId } : { transactionId, name };
    const wire = await request<WirePayeeFromWire>('POST', '/v1/me/payees/from-counterparty', {
      json,
    });
    return { payeeId: wire.payee.id, name: wire.payee.name, linesResolved: wire.linesResolved };
  },

  /** POST /v1/me/payees. Unset notes and linked account are omitted. */
  async create(body: PayeeCreateRequest): Promise<Payee> {
    return normalizePayee(await request<WirePayee>('POST', '/v1/me/payees', { json: body }));
  },

  /** PATCH /v1/me/payees/{id}: the changed keys only; `alternateName: null` clears it. */
  async patch(id: number, body: PayeePatchBody): Promise<Payee> {
    return normalizePayee(await request<WirePayee>('PATCH', `/v1/me/payees/${id}`, { json: body }));
  },

  /** PUT /v1/me/payees/{id}/category-defaults/{virtualAccountId} (MM-D-126). */
  async setCategoryDefault(
    id: number,
    virtualAccountId: number,
    categoryId: number,
  ): Promise<PayeeCategoryDefault> {
    const body: PayeeCategoryDefaultRequest = { categoryId };
    return normalizeCategoryDefault(
      await request<WirePayeeCategoryDefault>(
        'PUT',
        `/v1/me/payees/${id}/category-defaults/${virtualAccountId}`,
        { json: body },
      ),
    );
  },

  /** DELETE /v1/me/payees/{id}/category-defaults/{virtualAccountId} → 204. */
  async clearCategoryDefault(id: number, virtualAccountId: number): Promise<void> {
    await request<void>('DELETE', `/v1/me/payees/${id}/category-defaults/${virtualAccountId}`);
  },

  /** DELETE /v1/me/payees/{id} — archives */
  async archive(id: number): Promise<Payee> {
    return normalizePayee(await request<WirePayee>('DELETE', `/v1/me/payees/${id}`));
  },

  /** GET /v1/me/payees/{id}/aliases */
  async aliases(id: number): Promise<PayeeAlias[]> {
    const rows = await request<WirePayeeAlias[]>('GET', `/v1/me/payees/${id}/aliases`);
    return rows.map(normalizePayeeAlias);
  },

  /** POST /v1/me/payees/{id}/aliases — an exact spelling. */
  async addAlias(id: number, payeeNorm: string): Promise<PayeeAlias> {
    const json: PayeeAliasCreateRequest = { matchType: 'EXACT', payee: payeeNorm };
    return normalizePayeeAlias(
      await request<WirePayeeAlias>('POST', `/v1/me/payees/${id}/aliases`, { json }),
    );
  },

  /**
   * POST /v1/me/payees/{id}/aliases with a type (TMMAPI-0160 U3, place is the payee's as of
   * TMMAPI-0164 U2): an `EXACT` alias names a spelling (`payee`); `CONTAINS` / `REGEX` carry
   * `pattern`. `position` (0-based) places the payee when this is its first pattern alias; omitted
   * means last and is ignored when the payee is already in the order. `422 PATTERN_INVALID`.
   */
  async createAlias(
    id: number,
    body: { matchType: AliasMatchType; text: string; position?: number | undefined },
  ): Promise<PayeeAlias> {
    const json: PayeeAliasCreateRequest =
      body.matchType === 'EXACT'
        ? { matchType: 'EXACT', payee: body.text }
        : {
            matchType: body.matchType,
            pattern: body.text,
            ...(body.position !== undefined ? { position: body.position } : {}),
          };
    return normalizePayeeAlias(
      await request<WirePayeeAlias>('POST', `/v1/me/payees/${id}/aliases`, { json }),
    );
  },

  /** PATCH /v1/me/payees/{id}/aliases/{aliasId}: a pattern alias's new pattern (its type is kept). */
  async patchAlias(id: number, aliasId: number, pattern: string): Promise<PayeeAlias> {
    const json: PayeeAliasPatchRequest = { pattern };
    return normalizePayeeAlias(
      await request<WirePayeeAlias>('PATCH', `/v1/me/payees/${id}/aliases/${aliasId}`, { json }),
    );
  },

  /** POST /v1/me/payees/{id}/aliases/preview: the dry run; nothing is saved. */
  async previewAlias(
    id: number,
    body: { matchType: AliasMatchType; pattern: string },
  ): Promise<AliasPreview> {
    const json: PayeeAliasPreviewRequest = body;
    return normalizeAliasPreview(
      await request<WireAliasPreview>('POST', `/v1/me/payees/${id}/aliases/preview`, { json }),
    );
  },

  /**
   * POST /v1/me/payees/{id}/aliases/suggest-pattern (AI feature `PAYEE_PATTERN`): AI's proposal
   * and its dry run; nothing is saved. `409 AI_DISABLED {reason}` when AI can't run.
   */
  async suggestPattern(id: number, description: string): Promise<PatternSuggestion> {
    const json: PayeePatternSuggestRequest = { description };
    return normalizePatternSuggestion(
      await request<WirePatternSuggestion>('POST', `/v1/me/payees/${id}/aliases/suggest-pattern`, {
        json,
      }),
    );
  },

  /** GET /v1/me/payee-order: the payees that compete, highest first. Exact-only payees are absent. */
  async order(): Promise<PayeeOrderRow[]> {
    return normalizeOrderRows(await request<WireOrderRow[]>('GET', '/v1/me/payee-order'));
  },

  /** POST /v1/me/payee-order/reorder: the full ordered payee list (else `409 REORDER_SET_MISMATCH`). */
  async reorder(payeeIds: number[]): Promise<PayeeOrderRow[]> {
    return normalizeOrderRows(
      await request<WireOrderRow[]>('POST', '/v1/me/payee-order/reorder', {
        json: { payeeIds },
      }),
    );
  },

  /**
   * POST /v1/me/payee-patterns/{aliasId}/apply: names the existing lines the pattern wins. It never
   * moves hand-named lines or lines an exact alias names.
   */
  async applyPattern(aliasId: number): Promise<number> {
    const result = await request<WireApplyResponse>(
      'POST',
      `/v1/me/payee-patterns/${aliasId}/apply`,
    );
    return result.moved;
  },

  /** POST /v1/me/payee-order/reresolve[?dryRun=true]: the lines whose winner changed. */
  async reresolve(dryRun: boolean): Promise<number> {
    const result = await request<WireReresolve>('POST', '/v1/me/payee-order/reresolve', {
      ...(dryRun ? { query: { dryRun: true } } : {}),
    });
    return result.moved;
  },

  /** GET /v1/me/payees/{id}/export.xlsx?from&to: the payee's lines in the range. */
  async exportXlsx(
    id: number,
    window: { from?: string | undefined; to?: string | undefined } = {},
  ): Promise<DownloadResult> {
    return download(`/v1/me/payees/${id}/export.xlsx`, {
      query: { from: window.from, to: window.to },
    });
  },

  /**
   * DELETE /v1/me/payees/{id}/aliases/{aliasId}?dryRun=true (TMMAPI-0181 U3): what removing the
   * alias would do to the lines it named; nothing is changed.
   */
  async previewAliasRemoval(id: number, aliasId: number): Promise<AliasRemovalPreview> {
    const w = await request<WireAliasRemovalPreview>(
      'DELETE',
      `/v1/me/payees/${id}/aliases/${aliasId}`,
      { query: { dryRun: true } },
    );
    return {
      linesAffected: w.linesAffected,
      wouldRename: w.wouldRename,
      wouldUnname: w.wouldUnname,
      lastPatternAlias: w.lastPatternAlias,
    };
  },

  /**
   * DELETE /v1/me/payees/{id}/aliases/{aliasId}?lines=KEEP|RERESOLVE (TMMAPI-0181 U3): `KEEP` leaves
   * the lines it named on this payee; `RERESOLVE` re-matches them. Hand-named lines never change.
   */
  async removeAlias(id: number, aliasId: number, lines: AliasRemovalLines): Promise<void> {
    await request<void>('DELETE', `/v1/me/payees/${id}/aliases/${aliasId}`, {
      query: { lines },
    });
  },

  /** POST /v1/me/payees/{id}/merge */
  async merge(id: number, intoPayeeId: number): Promise<void> {
    const body: PayeeMergeRequest = { intoPayeeId };
    await request('POST', `/v1/me/payees/${id}/merge`, { json: body });
  },

  /** PUT /v1/me/accounts/{accountId}/transactions/{txId}/payee — the payee or a new name, never both. */
  async nameOnLine(
    accountId: number,
    txId: number,
    body: NameOnLineBody,
  ): Promise<OwnerRegisterLine> {
    const json: PayeeNameOnLineRequest =
      'payeeId' in body ? { payeeId: body.payeeId } : { name: body.name };
    if (body.scope !== undefined) json.scope = body.scope;
    return normalizeOwnerRegisterLine(
      await request<WireOwnerRegisterLine>(
        'PUT',
        `/v1/me/accounts/${accountId}/transactions/${txId}/payee`,
        { json },
      ),
    );
  },

  /** DELETE /v1/me/accounts/{accountId}/transactions/{txId}/payee */
  async clearOnLine(accountId: number, txId: number): Promise<OwnerRegisterLine> {
    return normalizeOwnerRegisterLine(
      await request<WireOwnerRegisterLine>(
        'DELETE',
        `/v1/me/accounts/${accountId}/transactions/${txId}/payee`,
      ),
    );
  },
};
